// frontend/src/TechnicianPortalApp.js

import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';

// Contextos
import { TechnicianAuthProvider, useTechnicianAuth } from './contexts/TechnicianAuthContext';

// Componentes de layout
import TechnicianLayout from './layouts/TechnicianLayout';

// Páginas
import TechnicianLogin from './pages/technician/TechnicianLogin';
import TechnicianDashboard from './pages/technician/TechnicianDashboard';
import TechnicianInstallations from './pages/technician/TechnicianInstallations';
import TechnicianTickets from './pages/technician/TechnicianTickets';
import TechnicianInventory from './pages/technician/TechnicianInventory';
import TechnicianReports from './pages/technician/TechnicianReports';
import TechnicianProfile from './pages/technician/TechnicianProfile';
import TechnicianSchedule from './pages/technician/TechnicianSchedule';
import InstallationComplete from './pages/technician/InstallationComplete';
import TicketDetails from './pages/technician/TicketDetails';

// Componentes de utilidad
import TechnicianPrivateRoute from './components/technician/TechnicianPrivateRoute';

// Página 404 simple para técnicos
const TechnicianNotFound = () => (
    <div style={{
        padding: '2rem',
        textAlign: 'center',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
    }}>
        <h1>404 - Página no encontrada</h1>
        <p>La página que buscas no existe en el portal del técnico.</p>
        <button onClick={() => window.location.href = '/technician/dashboard'}>
            Ir al Dashboard
        </button>
    </div>
);

// Tema personalizado para el portal técnico
const technicianTheme = createTheme({
    palette: {
        primary: {
            main: '#2196f3', // Azul más técnico
            light: '#64b5f6',
            dark: '#1976d2',
        },
        secondary: {
            main: '#ff9800', // Naranja para alertas
            light: '#ffb74d',
            dark: '#f57c00',
        },
        success: {
            main: '#4caf50',
        },
        warning: {
            main: '#ff9800',
        },
        error: {
            main: '#f44336',
        },
        background: {
            default: '#f5f5f5',
            paper: '#ffffff',
        },
    },
    typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h4: {
            fontWeight: 600,
        },
        h5: {
            fontWeight: 600,
        },
        h6: {
            fontWeight: 600,
        },
    },
    components: {
        MuiCard: {
            styleOverrides: {
                root: {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    borderRadius: 8,
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    textTransform: 'none',
                    fontWeight: 500,
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                },
            },
        },
    },
});

// CORREGIDO: Componente interno que usa el contexto correctamente
const TechnicianPortalRoutes = () => {
    console.log('TechnicianPortalRoutes - Renderizando');

    return (
        <Routes>
            {/* Ruta de login */}
            <Route path="/login" element={<TechnicianLogin />} />

            {/* Rutas protegidas */}
            <Route
                path="/"
                element={
                    <TechnicianPrivateRoute>
                        <TechnicianLayout />
                    </TechnicianPrivateRoute>
                }
            >
                {/* Dashboard */}
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<TechnicianDashboard />} />

                {/* Instalaciones */}
                <Route path="installations" element={<TechnicianInstallations />} />
                <Route path="installations/:id/complete" element={<InstallationComplete />} />

                {/* Tickets de soporte */}
                <Route path="tickets" element={<TechnicianTickets />} />
                <Route path="tickets/:id" element={<TicketDetails />} />

                {/* Inventario */}
                <Route path="inventory" element={<TechnicianInventory />} />

                {/* Reportes */}
                <Route path="reports" element={<TechnicianReports />} />

                {/* Horario */}
                <Route path="schedule" element={<TechnicianSchedule />} />

                {/* Perfil */}
                <Route path="profile" element={<TechnicianProfile />} />
            </Route>

            {/* Página 404 */}
            <Route path="*" element={<TechnicianNotFound />} />
        </Routes>
    );
};

const TechnicianPortalApp = () => {
    console.log('TechnicianPortalApp - Renderizando');

    return (
        <ThemeProvider theme={technicianTheme}>
            <CssBaseline />
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                <TechnicianAuthProvider>
                    <TechnicianPortalRoutes />
                </TechnicianAuthProvider>
            </LocalizationProvider>
        </ThemeProvider>
    );
};

export default TechnicianPortalApp;